/**
 * Reading a posting against the record: the pure parts.
 *
 * The API says which of the seeker's terms the posting uses ("have") and
 * which distinctive terms it asks for that the resume lacks ("lack"). These
 * helpers put those marks into the text without touching a word of it.
 */

export type Mark = "have" | "lack" | null;

export interface Segment {
  text: string;
  mark: Mark;
}

/** A term is a whole token: "test" must not light up "testing", nor "c#" the "c" of "c++". */
const EDGE = "[a-z0-9+#]";

function escape(term: string): string {
  return term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * The regex that finds every marked term at once. Longer terms first, so
 * "800-53" wins over "53" where both are terms. Null when there is nothing
 * to mark, so the caller can skip the work.
 */
export function markPattern(terms: string[]): RegExp | null {
  const clean = [...new Set(terms.map((t) => t.trim().toLowerCase()).filter(Boolean))]
    .sort((a, b) => b.length - a.length || a.localeCompare(b));
  if (clean.length === 0) return null;
  return new RegExp(`(?<!${EDGE})(?:${clean.map(escape).join("|")})(?!${EDGE})`, "gi");
}

/**
 * One line of the posting, cut into plain and marked runs. A term in both
 * lists is marked "have" -- the seeker has it, whatever the gap list thinks.
 */
export function segments(line: string, have: string[], lack: string[]): Segment[] {
  const pattern = markPattern([...have, ...lack]);
  if (!pattern) return line ? [{ text: line, mark: null }] : [];
  const has = new Set(have.map((t) => t.toLowerCase()));
  const out: Segment[] = [];
  let at = 0;
  for (const m of line.matchAll(pattern)) {
    const start = m.index ?? 0;
    if (start > at) out.push({ text: line.slice(at, start), mark: null });
    out.push({ text: m[0], mark: has.has(m[0].toLowerCase()) ? "have" : "lack" });
    at = start + m[0].length;
  }
  if (at < line.length) out.push({ text: line.slice(at), mark: null });
  return out;
}

/** The posting's lines as the board wrote them, blank ones dropped. */
export function lines(text: string): string[] {
  return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
}

/**
 * A date in a reader's words, relative to today: "today", "yesterday",
 * "5 days ago", then the calendar date once it is more than a month old.
 */
export function dateWords(iso: string | null, today: Date = new Date()): string | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const days = Math.floor((today.getTime() - then.getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 31) return `${days} days ago`;
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric", year: then.getFullYear() === today.getFullYear() ? undefined : "numeric" });
}
